/**
 * @NOTE: POC on replacement in favor of "axios-retry" [1]
 *    pros: well structure configuration, more business (exponential retry, etc)
 *    cons: not agnostic ("axios" dependent)
 *    risk: will it work with our custom Axios interface instances?
 *  ↳ update: will prefer a major break by replacing "axios" with "ofetch"
 *
 * @link https://www.npmjs.com/package/axios-retry [1]
 */
import AccountRequest from '~/utils/infra/account/request'
import AccountAPIError from '~/utils/infra/account/error'
import BaseRequest from '~/utils/infra/base/request'
import ClientRequest from '~/utils/infra/client/request'
import HamacRequest from '~/utils/infra/hamac/request'
import TransatRequest from '~/utils/infra/transat/request'
import GedRequest from '~/utils/infra/ged/request'
import ExportsRequest from '~/utils/infra/exports/request'
import PrepaPaieRequest from '~/utils/infra/prepa-paie/request'
import CssRequest from '~/utils/infra/css/request'
import { EVENTS as ACCOUNT_EVENTS, observer as accountObserver } from '~/utils/infra/account/types'
import { gte, not, path, promiseTry, prop, toString, __ } from '~/utils/lib/core/prelude'
import { ERRORS as COMMON_ERRORS, MAXIMUM_RETRY_ALLOWED } from '~/utils/infra/common/types'
import Console from '~/utils/lib/core/console'
import { useStore_account_authentication } from '~/stores/account/authentication'

const console = new Console('plugin:retry manager')

/**
 * @name RetryManager
 * @public
 * @desc
 *    Concrete Singleton visitor reducing request instance dynamic polymorphism
 */
export const RetryManager = {
  /**
   * @function visit
   * @param {*} self
   * @return {Promise<any>}
   */
  visit(self) {
    return promiseTry(() => validate(self))
      .then(() => retry(self))
  }
}
/**
 * @function validate
 * @param {*} request
 * @throws {Error}
 * @return {void}
 */
 function validate(request) {
  switch (true) {
    case request instanceof BaseRequest:
    case request instanceof AccountRequest:
    case request instanceof HamacRequest:
    case request instanceof ClientRequest:
    case request instanceof TransatRequest:
    case request instanceof GedRequest:
    case request instanceof ExportsRequest:
    case request instanceof PrepaPaieRequest:
    case request instanceof CssRequest:

      return

    default:

      throw new Error(COMMON_ERRORS.UNKNOWN_REQUEST_INSTANCE_TYPE)
  }
}
/**
 * @function retry
 * @param {AccountRequest|BaseRequest|HamacRequest|ClientRequest} request
 * @return {Promise<any>}
 */
async function retry(request) {
  const authentication = useStore_account_authentication()
  const fromRefresh
    =  request instanceof AccountRequest
    && request.config.url === 'refresh'

  // (?) Heads up: sensitive script here
  //
  // We are validating the returned error via an Applicative FP pattern
  // ie: if an error occurs within this pseudo-monad, it will break the
  // sequencial flow
  //
  // However, the validation process does not wrap few lines below.
  //
  // Why? Because this retry process MUST bear a recursivity in mind (ie. some /// old or new request **being executed**, and thus being potentially be
  // intercepted and making the recursive loop).
  //
  // So if a nested execution breaks the pseudo-monadic process, it will:
  //    1. trigger an error, and being catched here
  //    2. being catched by its more higher scope (recursivity)
  //
  // We do not want a multiplication of error handling. (All-in-all, we are
  // dealing with JavaScript limitation lacking Bind/Applicative/Monadic traits)
  try {

    validateAttemptLimitation(request)

    validateAuthorizeOnly(request)

    validatePolicyInfringement(request)

  } catch (err) {

    return handleValidationThrow(err, request)
  }

  // relaunch refresh request, leading to its own sub-cascade of `execCount`
  // (recursion induced)
  if (fromRefresh)
    return request.exec()

  // launch a refresh token...
  // (recursion induced)
  await authentication.refresh()

  // ...that potentially lead to the success of the source request
  return request.exec()
}
/**
 * @XXX "compte-svc" micro service not explicitely return a dedicated
 *      response for session renewal
 *    ↳ patch surgery: we will assume that every HTTP error here will lead
 *      to a policy infringement error
 * @function isPolicyInfringementError
 * @public
 * @param {AccountAPIError|any} error
 * @returns {boolean}
 */
export function isPolicyInfringementError(error) {
  return (error instanceof AccountAPIError)
}
/**
 * @function validateAttemptLimitation
 * @param {AccountRequest|BaseRequest|HamacRequest|ClientRequest} request
 * @throws {Error}
 * @return {void}
 */
function validateAttemptLimitation(request) {
  const limitReached = gte(request.config.execCount, MAXIMUM_RETRY_ALLOWED)

  if (limitReached)
    throw new Error(COMMON_ERRORS.MAXIMUM_REQUEST_RETRY_REACHED)
}
/**
 * @function validateAuthorizeOnly
 * @param {AccountRequest|BaseRequest|HamacRequest|ClientRequest} request
 * @throws {Error}
 * @return {void}
 */
function validateAuthorizeOnly(request) {
  // The trick with the micro service ecosystem is here: some business request
  // returns a 401 HTTP StatusCode, thus leading to a solicitation of the
  // RetryManager
  //
  // In a nutshell, regarding these 401 error:
  //
  //    a) coherent Unauthorized made by an "authorized()" request → most of the
  //       case it will be caused from an expired token
  //
  //    b) incoherent business Left-result from a request **without**
  //       "authorized" → we should not use the 401 StatusCode here, because in
  //       some cases, parsing are made **onto** the results. So we have to
  //       make conditional validation checking this very case (leading to
  //       complex-not-generic retry process).
  //          ↳ We should just instead use another status code, or if some
  //            Left-result needs to be parsed, just throw a success StatusCode
  //            with a contextualised payload
  //
  //    c) incoherent business Left-result from the "account::GET /refresh" →
  //       this not "authorized()" request (so normally acquaintance of the
  //       b) case) **needs** another layering treatement. The business result
  //       is not parsed in the application, but in this very RetryManager
  //       instead.
  //          ↳ Identical proposal to the b) case
  //
  //    d) incoherent business Left-result from the "account::GET /refresh" BUT
  //       with a returned HTTP StatusCode 404...
  //          ↳ So the only solution is to use the Retry Manager any time the
  //            URL is from the "/refresh" route (note that this workaround
  //            is patched into the Account interceptor)
  //          ↳ @NOTE: rework the structure between Interceptor <-> Retry
  //            Manager to use the latter on every HTTP StatusCode?
  //
  const fromRefresh
    =  request instanceof AccountRequest
    && request.config.url === 'refresh'

  if (fromRefresh)
    return

  const authorizedCall = path(['config', 'withAuthorization'], request)

  if (not(authorizedCall))
    throw new Error(COMMON_ERRORS.ANONYMOUS_REQUEST_THROWED_401)
}
/**
 * @function validatePolicyInfringement
 * @param {AccountRequest|BaseRequest|HamacRequest|ClientRequest} request
 * @throws {AccountAPIError}
 * @return {void}
 */
function validatePolicyInfringement(request) {
  // Only "account::GET /refresh" should be subjected to a policy check
  const fromRefresh
    =  request instanceof AccountRequest
    && request.config.url === 'refresh'

  if (not(fromRefresh))
    return

  // Checking through the "account" micro service error dictionary, some
  // are related to a policy infringement, some are not
  const error = prop('error', request)

  if (isPolicyInfringementError(error))
    throw error
}
/**
 * @function handleValidationThrow
 * @static
 * @param {any} err
 * @param {AccountRequest|BaseRequest|HamacRequest|ClientRequest} request
 * @return {Promise<any>}
 */
function handleValidationThrow(err, request) {
  const nativeRequestError = prop('error', request)

  switch (true) {

    case
      (
          err instanceof Error
      &&  toString(err) === COMMON_ERRORS.MAXIMUM_REQUEST_RETRY_REACHED
      ):

      console.warn(
        `maximum retry for request reached`
      )

      return Promise.reject(err)


    case
      (
        isPolicyInfringementError(err)
      ):

      console.warn(
        `catched policy infringement from "compte-svc"`
      )

      return accountObserver.emit(ACCOUNT_EVENTS.ON_REFRESH_POLICY_INFRINGEMENT)
        .finally(() => Promise.reject(nativeRequestError))

    case
      (   err instanceof Error
      &&  toString(err) === COMMON_ERRORS.ANONYMOUS_REQUEST_THROWED_401
      ):

      console.log(
        `catched a 401 on a non-authorized call,`,
        `ideally the micro-service should return a contextualised error`,
        `with a success HTTP StatusCode instead`
      )

      return Promise.reject(nativeRequestError)

    default:

      console.error(
        `catched an unkown error on retry manager validation,`,
        `should NEVER happen as it will completely break the user experience`,
        err
      )

      return promiseTry(() => showError(err))
  }
}
